import {customElement, property, query, state} from "lit/decorators.js";
import {UnLitElement} from "../../../common/elements";
import {html, type PropertyValues, type TemplateResult} from "lit";
import {Dictionary} from "../dictionary";
import {trackingTextlinkInteract} from "../../../tracking/trackingAttributes";
import type {Nullable} from "../../../bootstrap/common/types";


enum ReadingState {
    NONE, READING, PAUSED
}

@customElement("eop-read-aloud-button")
export class EopReadAloudButton extends UnLitElement {

    @query("audio")
    private audioPlayer: HTMLAudioElement;

    @property({attribute: "audio-file"})
    private audioFile: string;

    @state()
    private state: ReadingState = ReadingState.NONE;

    private dictionary: Dictionary;
    private playPromise: Nullable<Promise<void>> = null;

    public constructor() {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);
    }

    public disconnectedCallback(): void {
        super.disconnectedCallback();
    }

    public render(): TemplateResult {
        return html`
            <audio>
                <source src=${this.audioFile}>
            </audio>
            ${this.renderButton()}
        `;
    }

    private renderButton(): TemplateResult {
        if (this.state === ReadingState.READING) {
            return this.renderPauseButton();
        }
        if (this.state === ReadingState.PAUSED) {
            return this.renderResumeButton();
        }
        return this.renderStartButton();
    }

    private renderStartButton(): TemplateResult {
        return html`
            <button class="link read-aloud-button start-reading" @click=${this.start} ${trackingTextlinkInteract("read-aloud")}
            >${this.dictionary.translate("MSG_READ_ALOUD_BUTTON")}</button>
        `;
    }

    private renderPauseButton(): TemplateResult {
        return html`
            <button class="link read-aloud-button pause-reading" @click=${this.pause} ${trackingTextlinkInteract("pause-reading")}
            >${this.dictionary.translate("MSG_READ_ALOUD_PAUSE_BUTTON")}</button>
        `;
    }

    private renderResumeButton(): TemplateResult {
        return html`
            <button class="link read-aloud-button resume-reading" @click=${this.resume} ${trackingTextlinkInteract("resume-reading")}
            >${this.dictionary.translate("MSG_READ_ALOUD_RESUME_BUTTON")}</button>
        `;
    }

    protected firstUpdated(_changedProperties: PropertyValues) : void{
        super.firstUpdated(_changedProperties);
        this.audioPlayer.addEventListener("ended", () => {
            this.state = ReadingState.NONE;
        });
    }

    private start(): void {
        this.state = ReadingState.READING;
        this.playPromise = this.audioPlayer.play();
    }

    private async pause(): Promise<void> {
        this.state = ReadingState.PAUSED;
        if (this.playPromise) {
            await this.playPromise;
        }
        this.audioPlayer.pause();
    }

    private resume(): void {
        this.state = ReadingState.READING;
        this.playPromise = this.audioPlayer.play();
    }
}